import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useApi } from '../lib/useApi';
import { apiRequest, ApiError } from '../lib/api';
import { shortDate } from '../lib/format';
import { PublicHeader } from '../components/PublicHeader';
import { SiteFooter } from '../components/SiteFooter';
import { Alert, Button, LoadingBlock } from '../components/ui';
import '../styles/landing.css';
import '../styles/public.css';
import '../styles/inspections.css';

/**
 * The tenant's side, reached by a link rather than a login.
 *
 * There is no tenant portal yet, and waiting for one would have meant shipping
 * an inspection module that only ever records the landlord's opinion — which
 * is the thing it exists to improve on. A one-off link costs the tenant
 * nothing: no account, no password, no app.
 *
 * Written for someone who has never seen this product. It says who is asking,
 * what it is for, and what happens either way, before it asks for anything.
 *
 * Photos load as thumbnails and open full size on tap, exactly as the
 * landlord's screen does — the tenant is at least as likely to be on a phone
 * and a bundle.
 */

interface Item {
  id: string; area: string; label: string; condition: string | null;
  notes: string | null; photos: { id: string }[];
}
interface View {
  inspection: {
    id: string; reference: string; inspection_type: string; status: string;
    inspection_date: string; property_name: string; unit_number: string;
    tenant_name: string | null; account_name: string; inspector_name: string | null;
    notes: string | null; tenant_confirmed_at: string | null; tenant_decision: string | null;
  };
  items: Item[];
  meters: { utility: string; meter_number: string | null; reading: string }[];
}

const TYPE_LABEL: Record<string, string> = {
  move_in: 'move-in', move_out: 'move-out', periodic: 'routine',
};

export function InspectionConfirm() {
  const { token = '' } = useParams();
  const view = useApi<View>(`/api/v1/inspection-confirmations/${token}`);
  const [comments, setComments] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<'accepted' | 'disputed' | null>(null);
  const [lightbox, setLightbox] = useState<string | null>(null);

  const decide = async (decision: 'accepted' | 'disputed') => {
    if (decision === 'disputed' && comments.trim().length < 3) {
      setError('Please say what you disagree with, so it is on the record.');
      return;
    }
    setBusy(true); setError(null);
    try {
      await apiRequest(`/api/v1/inspection-confirmations/${token}`, {
        method: 'POST',
        body: { decision, ...(comments.trim() ? { comments: comments.trim() } : {}) },
      });
      setOutcome(decision);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'That could not be submitted. Try again.');
    } finally { setBusy(false); }
  };

  return (
    <div className="lp mk">
      <PublicHeader />

      <div className="insp-confirm">
        {view.loading && <LoadingBlock />}

        {view.error && (
          <div className="mk-success">
            <div className="mk-success-mark" aria-hidden
              style={{ background: 'var(--brand-100)', color: 'var(--brand-700)' }}>?</div>
            <h1 className="mk-h2">This link is no longer valid</h1>
            <p className="mk-p">
              Confirmation links can be used once and expire after fourteen days. Ask your
              landlord or agent to send a fresh one.
            </p>
            <div className="mk-cta-row" style={{ marginTop: 'var(--space-6)' }}>
              <Link className="btn btn-primary" to="/">Go to the homepage</Link>
            </div>
          </div>
        )}

        {view.data && outcome && (
          <div className="mk-success">
            <div className="mk-success-mark" aria-hidden>✓</div>
            <h1 className="mk-h2">
              {outcome === 'accepted' ? 'Thank you — that is confirmed' : 'Your disagreement is recorded'}
            </h1>
            <p className="mk-p">
              {/*
                NOT "your landlord has been notified", which is what this said.
                Confirming writes the decision, the date and any comment onto the
                inspection and into the audit trail - and sends nothing. There is
                no email, no inbox item and no attention badge, so the landlord
                learns about it when they next open that inspection. The
                landlord's own screen already admits this ("Nothing has been sent
                automatically"); the tenant's should not claim the opposite.
              */}
              {outcome === 'accepted'
                ? 'Your answer is saved against the inspection and your landlord can see it '
                  + 'there. Keep a copy of this page for your records; the same record can be '
                  + 'produced later if anything is ever questioned.'
                : 'What you wrote has been saved with the inspection and your landlord can see it. '
                  + 'Nothing has been deducted from any deposit — this is a record of the condition, '
                  + 'not a decision about money.'}
            </p>
          </div>
        )}

        {view.data && !outcome && (
          <>
            <header className="insp-confirm-head">
              <p className="mk-eyebrow">{view.data.inspection.account_name}</p>
              <h1 className="mk-title">Please check this {TYPE_LABEL[view.data.inspection.inspection_type]} record</h1>
              <p className="mk-lede">
                This is the condition of {view.data.inspection.property_name}{' '}
                {view.data.inspection.unit_number} as recorded on{' '}
                {shortDate(view.data.inspection.inspection_date)}
                {view.data.inspection.inspector_name && ` by ${view.data.inspection.inspector_name}`}.
                Read through it and say whether you agree. If something is wrong, say so — it
                will be kept with the record.
              </p>
            </header>

            {view.data.inspection.notes && (
              <div style={{ marginBottom: 'var(--space-5)' }}>
                <Alert tone="info">{view.data.inspection.notes}</Alert>
              </div>
            )}

            <div className="insp-confirm-list">
              {groupByArea(view.data.items).map(([area, items]) => (
                <section key={area} className="insp-confirm-area">
                  <h2>{area}</h2>
                  {items.map(it => (
                    <div key={it.id} className={`insp-confirm-line c-${it.condition ?? 'none'}`}>
                      <div className="insp-confirm-line-head">
                        <span>{it.label}</span>
                        <span className={`insp-tag ${it.condition ?? ''}`}>
                          {(it.condition ?? 'not recorded').replace(/_/g, ' ')}
                        </span>
                      </div>
                      {it.notes && <p className="insp-confirm-note">{it.notes}</p>}
                      {it.photos.length > 0 && (
                        <div className="insp-photos">
                          {it.photos.map(p => (
                            <button key={p.id} type="button" className="insp-thumb"
                              onClick={() => setLightbox(p.id)} aria-label="View photo">
                              <img loading="lazy" alt=""
                                src={`/api/v1/inspection-confirmations/${token}/photos/${p.id}?size=thumb`} />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </section>
              ))}
            </div>

            {view.data.meters.length > 0 && (
              <section className="insp-confirm-area">
                <h2>Meter readings</h2>
                {view.data.meters.map((m, n) => (
                  <div key={n} className="insp-confirm-line">
                    <div className="insp-confirm-line-head">
                      <span>{m.utility}{m.meter_number ? ` · ${m.meter_number}` : ''}</span>
                      <strong>{m.reading}</strong>
                    </div>
                  </div>
                ))}
              </section>
            )}

            <section className="insp-confirm-decide">
              <h2>Do you agree with this record?</h2>
              {error && <Alert tone="danger">{error}</Alert>}
              <textarea className="input" rows={3} value={comments}
                onChange={e => setComments(e.target.value)}
                placeholder="Anything you want on the record (required if you disagree)" />
              <div className="mk-cta-row" style={{ marginTop: 'var(--space-4)' }}>
                <Button loading={busy} onClick={() => void decide('accepted')}>
                  Yes, I agree
                </Button>
                <Button variant="secondary" loading={busy} onClick={() => void decide('disputed')}>
                  No, something is wrong
                </Button>
              </div>
              <p className="muted" style={{ marginTop: 'var(--space-4)' }}>
                Either answer is recorded and neither affects your deposit on its own. You can
                only answer once, so read through before you choose.
              </p>
            </section>
          </>
        )}
      </div>

      {lightbox && (
        <div className="insp-lightbox" role="dialog" aria-label="Photo"
          onClick={() => setLightbox(null)}>
          <img src={`/api/v1/inspection-confirmations/${token}/photos/${lightbox}`} alt="" />
        </div>
      )}

      <SiteFooter />
    </div>
  );
}

function groupByArea(items: Item[]): [string, Item[]][] {
  const map = new Map<string, Item[]>();
  for (const it of items) {
    const list = map.get(it.area) ?? [];
    list.push(it);
    map.set(it.area, list);
  }
  return [...map.entries()];
}
